import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';

// Reusable attachment thumbnails component
function AttachmentThumbnails({ attachments }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
      {attachments.map(att => (
        <a
          key={att.id}
          href={`${API}/contact/attachments/${att.id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'block', border: '1px solid rgba(148,163,184,0.2)', borderRadius: '4px', overflow: 'hidden' }}
          title={att.file_name}
        >
          <img
            src={`${API}/contact/attachments/${att.id}`}
            alt={att.file_name}
            style={{ width: '64px', height: '64px', objectFit: 'cover', display: 'block' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        </a>
      ))}
    </div>
  );
}

export default function MessagesTab({ t, formatDateTime, addToast }) {
  // Data state
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Search state
  const [msgSearch, setMsgSearch] = useState('');
  const [msgSearchDebounced, setMsgSearchDebounced] = useState('');
  const msgSearchTimer = useRef(null);

  // Status filter state
  const [statusFilter, setStatusFilter] = useState('');

  // Reply modal state
  const [replyModal, setReplyModal] = useState({ open: false, messageId: null, message: null });
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [replyPreviews, setReplyPreviews] = useState([]);
  const replyFileRef = useRef(null);

  // Thread state
  const [threadData, setThreadData] = useState({ messageId: null, message: null, replies: [] });

  // Debounced search handler — 300ms, resets to page 1
  const handleMsgSearch = (val) => {
    setMsgSearch(val);
    clearTimeout(msgSearchTimer.current);
    msgSearchTimer.current = setTimeout(() => {
      setMsgSearchDebounced(val);
      setPage(1);
    }, 300);
  };

  // Load messages from server
  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (msgSearchDebounced.trim()) params.q = msgSearchDebounced.trim();
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API}/contact`, { params });
      setMessages(res.data.data || []);
      const pag = res.data.pagination || {};
      setPage(pag.page || 1);
      setTotalPages(pag.totalPages || 1);
      setTotal(pag.total || 0);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, msgSearchDebounced, statusFilter]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Self-contained status update
  const updateMessageStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contact/${id}`, { status });
      loadMessages();
    } catch (err) {
      console.error('Failed to update message status:', err);
    }
  };

  // Reply file handling
  const handleReplyFileChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 3);
    setReplyFiles(files);
    const previews = files.map(f => ({ name: f.name, url: URL.createObjectURL(f) }));
    setReplyPreviews(previews);
  };

  const removeReplyFile = (idx) => {
    const newFiles = [...replyFiles];
    const newPreviews = [...replyPreviews];
    URL.revokeObjectURL(newPreviews[idx].url);
    newFiles.splice(idx, 1);
    newPreviews.splice(idx, 1);
    setReplyFiles(newFiles);
    setReplyPreviews(newPreviews);
  };

  const clearReplyFiles = () => {
    replyPreviews.forEach(p => URL.revokeObjectURL(p.url));
    setReplyFiles([]);
    setReplyPreviews([]);
  };

  const uploadAttachments = async (files) => {
    if (!files.length) return [];
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const res = await axios.post(`${API}/contact/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data.map(a => a.id);
  };

  // Pagination helpers
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <div>
      <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#D42B2B', marginBottom: '2rem' }}>{t.admin.contactMessages}</h2>

      {/* Search bar + Status filter */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', maxWidth: '400px', flex: '1 1 250px' }}>
          <input
            type="text"
            className="form-control"
            placeholder={t.admin.search?.placeholder || 'Search by name, phone, email\u2026'}
            value={msgSearch}
            onChange={e => handleMsgSearch(e.target.value)}
            style={{ paddingRight: '2.5rem' }}
          />
          {msgSearch && (
            <button
              onClick={() => { setMsgSearch(''); setMsgSearchDebounced(''); setPage(1); }}
              aria-label={t.common.close}
              style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94A3B8', fontSize: '1.1rem', cursor: 'pointer', padding: '0.25rem' }}
            >&times;</button>
          )}
        </div>
        <select
          className="form-select"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ width: 'auto', minWidth: '140px' }}
        >
          <option value="">{t.admin.search?.allStatuses || 'All'}</option>
          <option value="new">{t.admin.search?.statusNew || 'New'}</option>
          <option value="read">{t.admin.search?.statusRead || 'Read'}</option>
          <option value="responded">{t.admin.search?.statusResponded || 'Responded'}</option>
        </select>
      </div>

      {/* Pagination controls — top */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.85rem', color: '#94A3B8', whiteSpace: 'nowrap' }}>
            {t.admin?.search?.rowsPerPage || 'Rows per page'}:
          </label>
          <select
            className="form-select form-select-sm"
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            style={{ width: 'auto', minWidth: '70px' }}
          >
            {[10, 20, 30, 40, 50, 100, 200, 500, 1000, 2000].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#94A3B8' }}>
            {(t.admin?.search?.showing || '{from}\u2013{to} of {total}')
              .replace('{from}', rangeStart)
              .replace('{to}', rangeEnd)
              .replace('{total}', total)}
          </span>
          <div style={{ display: 'flex', gap: '0.35rem' }}>
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              {t.admin?.search?.prev || 'Previous'}
            </button>
            <span style={{ fontSize: '0.85rem', color: '#94A3B8', alignSelf: 'center', padding: '0 0.25rem' }}>
              Page {page} / {totalPages}
            </span>
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            >
              {t.admin?.search?.next || 'Next'}
            </button>
          </div>
        </div>
      </div>

      {/* Messages list with loading overlay */}
      <div style={{ position: 'relative' }}>
        {loading && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(12,18,32,0.6)', zIndex: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <div className="spinner-border text-danger" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gap: '1rem' }}>
          {messages.map(msg => (
            <div key={msg.id} style={{
              background: msg.status === 'new' ? 'rgba(148,163,184,0.05)' : '#111827',
              border: msg.status === 'new' ? '1px solid rgba(148,163,184,0.3)' : '1px solid rgba(148,163,184,0.15)',
              padding: '1.5rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div>
                  <div style={{ color: '#E8ECF0', fontSize: '1.1rem', marginBottom: '0.3rem' }}>
                    {msg.name}
                    {msg.status === 'new' && <span className="badge bg-primary ms-2" style={{ fontSize: '0.65rem' }}>{t.admin.newBadge}</span>}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#94A3B8' }}>
                    {msg.email} {msg.phone && `\u2022 ${msg.phone}`}
                  </div>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94A3B8' }}>
                  {formatDateTime(msg.created_at)}
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.9rem', color: '#D42B2B', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {msg.subject}
                </div>
                <div style={{ color: '#64748B', fontSize: '0.95rem', lineHeight: 1.6 }}>
                  {msg.message}
                </div>
                <AttachmentThumbnails attachments={msg.attachments} />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {msg.status === 'new' && (
                  <button className="btn btn-outline-primary btn-sm" onClick={() => updateMessageStatus(msg.id, 'read')}>
                    {t.admin.markRead}
                  </button>
                )}
                <button className="btn btn-primary btn-sm" onClick={() => {
                  setReplyModal({ open: true, messageId: msg.id, message: msg });
                  setReplyText('');
                  setReplyError('');
                  clearReplyFiles();
                }}>
                  {t.admin.reply}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={async () => {
                  if (threadData.messageId === msg.id) {
                    setThreadData({ messageId: null, message: null, replies: [] });
                  } else {
                    try {
                      const res = await axios.get(`${API}/contact/${msg.id}/thread`);
                      setThreadData({ messageId: msg.id, ...res.data });
                    } catch (err) { console.error(err); }
                  }
                }}>
                  {threadData.messageId === msg.id ? t.admin.hideThread : t.admin.viewThread}
                </button>
              </div>
              {/* Thread view */}
              {threadData.messageId === msg.id && (
                <div style={{ marginTop: '1rem', borderTop: '1px solid rgba(148,163,184,0.15)', paddingTop: '1rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#94A3B8', marginBottom: '0.5rem', fontWeight: 600 }}>{t.admin.originalMessage}</div>
                  <div style={{ background: 'rgba(148,163,184,0.05)', padding: '0.75rem', marginBottom: '0.75rem', borderLeft: '3px solid #64748B', fontSize: '0.9rem', color: '#94A3B8' }}>
                    <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>{msg.name} — {formatDateTime(msg.created_at)}</div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{threadData.message?.message || msg.message}</div>
                    <AttachmentThumbnails attachments={threadData.message?.attachments} />
                  </div>
                  {threadData.replies.map(reply => (
                    <div key={reply.id} style={{ background: reply.sender_role === 'admin' ? 'rgba(212,43,43,0.05)' : 'rgba(100,150,200,0.05)', padding: '0.75rem', marginBottom: '0.5rem', borderLeft: `3px solid ${reply.sender_role === 'admin' ? '#D42B2B' : '#64748B'}`, fontSize: '0.9rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <span style={{ color: reply.sender_role === 'admin' ? '#D42B2B' : '#94A3B8', fontWeight: 500 }}>
                          {reply.sender_name}
                          {reply.sender_role === 'admin' && <span className="badge bg-danger ms-2" style={{ fontSize: '0.55rem' }}>Admin</span>}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>{formatDateTime(reply.created_at)}</span>
                      </div>
                      <div style={{ color: '#E8ECF0', whiteSpace: 'pre-wrap' }}>{reply.body}</div>
                      <AttachmentThumbnails attachments={reply.attachments} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {messages.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#94A3B8' }}>
              {msgSearchDebounced ? (t.admin.search?.noResults || 'No results') : t.admin.noMessages}
            </div>
          )}
        </div>
      </div>

      {/* Pagination controls — bottom */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '1rem', gap: '0.75rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#94A3B8' }}>
            {(t.admin?.search?.showing || '{from}\u2013{to} of {total}')
              .replace('{from}', rangeStart)
              .replace('{to}', rangeEnd)
              .replace('{total}', total)}
          </span>
          <div style={{ display: 'flex', gap: '0.35rem' }}>
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              {t.admin?.search?.prev || 'Previous'}
            </button>
            <span style={{ fontSize: '0.85rem', color: '#94A3B8', alignSelf: 'center', padding: '0 0.25rem' }}>
              Page {page} / {totalPages}
            </span>
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            >
              {t.admin?.search?.next || 'Next'}
            </button>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {replyModal.open && replyModal.message && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => { if (!replySending) { setReplyModal({ open: false, messageId: null, message: null }); clearReplyFiles(); } }}>
          <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.2)', padding: '2rem', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}
               onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: "'Rajdhani', sans-serif", color: '#D42B2B', marginBottom: '1.5rem' }}>{t.admin.reply}</h3>
            <div style={{ marginBottom: '1rem', background: 'rgba(148,163,184,0.05)', padding: '0.75rem', borderLeft: '3px solid #64748B' }}>
              <div style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '0.25rem' }}>{t.admin.originalMessage}:</div>
              <div style={{ fontSize: '0.85rem', color: '#94A3B8', whiteSpace: 'pre-wrap' }}>{replyModal.message.message}</div>
              <AttachmentThumbnails attachments={replyModal.message.attachments} />
            </div>
            <textarea
              className="form-control"
              rows="6"
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder={t.admin.replyPlaceholder}
              disabled={replySending}
              style={{ marginBottom: '0.75rem' }}
            />
            {/* Attachment controls */}
            <div style={{ marginBottom: '1rem' }}>
              <input
                ref={replyFileRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                onChange={handleReplyFileChange}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => replyFileRef.current?.click()}
                disabled={replySending || replyFiles.length >= 3}
                style={{
                  background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                  color: '#94A3B8', padding: '0.35rem 0.8rem', fontSize: '0.75rem',
                  cursor: replySending || replyFiles.length >= 3 ? 'not-allowed' : 'pointer',
                  opacity: replyFiles.length >= 3 ? 0.5 : 1
                }}
              >
                {t.admin.attachImages || 'Attach Images'}
              </button>
              <span style={{ fontSize: '0.7rem', color: '#64748B', marginLeft: '0.5rem' }}>
                {t.admin.attachHint || 'Max 3 images, 5MB each'}
              </span>
              {replyPreviews.length > 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  {replyPreviews.map((p, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img
                        src={p.url}
                        alt={p.name}
                        style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '4px', border: '1px solid rgba(148,163,184,0.2)' }}
                      />
                      <button
                        type="button"
                        onClick={() => removeReplyFile(idx)}
                        aria-label={t.common.close}
                        style={{
                          position: 'absolute', top: '-6px', right: '-6px',
                          background: '#D42B2B', border: 'none', color: '#fff',
                          width: '18px', height: '18px', borderRadius: '50%',
                          fontSize: '0.65rem', lineHeight: '18px', textAlign: 'center',
                          cursor: 'pointer', padding: 0
                        }}
                      >&times;</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {replyError && (
              <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{replyError}</div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => { setReplyModal({ open: false, messageId: null, message: null }); clearReplyFiles(); }} disabled={replySending}>
                {t.common.cancel}
              </button>
              <button className="btn btn-primary btn-sm" disabled={replySending || !replyText.trim()} onClick={async () => {
                setReplySending(true);
                setReplyError('');
                try {
                  // Upload attachments first
                  const attachmentIds = await uploadAttachments(replyFiles);
                  await axios.post(`${API}/contact/${replyModal.messageId}/reply`, {
                    body: replyText,
                    attachment_ids: attachmentIds
                  });
                  setReplyModal({ open: false, messageId: null, message: null });
                  clearReplyFiles();
                  loadMessages();
                  addToast({ type: 'reply_sent', message: t.admin.replySuccess });
                } catch (err) {
                  setReplyError(err.response?.data?.error || t.admin.replyFailed);
                } finally {
                  setReplySending(false);
                }
              }}>
                {replySending ? t.admin.sending : t.admin.sendReply}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
